/**
 * Pricing utility functions for video processing
 */

const PER_MINUTE_RATE = 0.045;
const FIXED_FEE = 0.85;

/**
 * Calculate the total price for video processing
 * @param durationInSeconds - Video duration in seconds
 * @returns Total price as a string with 2 decimal places
 */
export function calculateVideoPrice(durationInSeconds: number): string {
  if (durationInSeconds <= 0) return "0.00";
  
  const minutes = Math.ceil(durationInSeconds / 60); // Round up to nearest minute
  const totalPrice = (minutes * PER_MINUTE_RATE) + FIXED_FEE;
  
  return totalPrice.toFixed(2);
}

/**
 * Get the pricing breakdown for display purposes
 * @param durationInSeconds - Video duration in seconds
 * @returns Object with pricing breakdown
 */
export function getPricingBreakdown(durationInSeconds: number) {
  if (durationInSeconds <= 0) {
    return {
      minutes: 0,
      perMinuteCost: 0,
      fixedFee: FIXED_FEE,
      totalPrice: 0
    };
  }
  
  const minutes = Math.ceil(durationInSeconds / 60);
  const perMinuteCost = minutes * PER_MINUTE_RATE;
  const totalPrice = perMinuteCost + FIXED_FEE;
  
  return {
    minutes,
    perMinuteCost: parseFloat(perMinuteCost.toFixed(2)),
    fixedFee: FIXED_FEE,
    totalPrice: parseFloat(totalPrice.toFixed(2))
  };
}

/**
 * Get the display text for pricing
 * @param durationInSeconds - Video duration in seconds
 * @returns Formatted pricing text
 */
export function getPricingDisplayText(durationInSeconds: number): string {
  if (durationInSeconds <= 0) return "Video duration not available";
  
  const breakdown = getPricingBreakdown(durationInSeconds);
  return `${breakdown.minutes} minutes × $${PER_MINUTE_RATE} per minute + $${FIXED_FEE} fixed fee`;
}

export { PER_MINUTE_RATE, FIXED_FEE };
